import type { CSSProperties } from "react";
import {
  formatClock,
  pickText,
  selectGuideTrack,
  useGuided,
  useGuidedOptional,
  useTourState,
} from "@heroncendre/player";

/**
 * ============================================================================
 *  DemoPanel.tsx — Les compteurs de la démo (`?demo=…`)
 * ----------------------------------------------------------------------------
 *  Pour discuter du parcours avec l'équipe : durée d'audio et nombre de mots par
 *  chapitre, et sa séquence de plans (instant, scène, trajet de la caméra).
 *  Repliable, discret, et absent d'une visite livrée — il ne sert qu'à la démo.
 *
 *  Tout est lu dans le document : aucune donnée de démo ici.
 * ==========================================================================*/

const boite: CSSProperties = {
  position: "absolute",
  // Sous le fil d'Ariane (12 px de marge + 44 px de zone touchable), pas dessus.
  top: 64,
  left: 12,
  maxWidth: "min(340px, calc(100vw - 24px))",
  maxHeight: "60vh",
  overflowY: "auto",
  padding: "6px 10px",
  borderRadius: 8,
  background: "rgba(17,17,17,0.72)",
  color: "#ddd",
  fontSize: 12,
  lineHeight: 1.5,
  pointerEvents: "auto",
  fontVariantNumeric: "tabular-nums",
};

/** Sans parcours valide (refusé par le player), il n'y a rien à compter. */
export function DemoPanel() {
  if (!useGuidedOptional()) return null;
  return <DemoPanelContenu />;
}

function DemoPanelContenu() {
  const { timeline } = useGuided();
  const tour = useTourState((s) => s.tour);
  const language = useTourState((s) => s.context.language);
  const stepIndex = useTourState((s) => s.guided.stepIndex);
  const actif = useTourState((s) => s.guided.state !== "idle");

  let totalSecondes = 0;
  let totalMots = 0;

  const lignes = timeline.steps.map((step, index) => {
    const choix = selectGuideTrack(tour, step.guideId, language);
    const secondes = choix?.track.durationSeconds ?? 0;
    const mots = choix?.track.transcript?.trim().split(/\s+/).filter(Boolean).length ?? 0;
    totalSecondes += secondes;
    totalMots += mots;
    return { step, index, secondes, mots, cues: choix?.guide.cues ?? [] };
  });

  return (
    <details style={boite}>
      <summary style={{ cursor: "pointer", minHeight: 24 }}>
        Démo — {formatClock(totalSecondes)} de voix off, {totalMots} mots
      </summary>
      {lignes.map(({ step, index, secondes, mots, cues }) => (
        <div key={step.id} style={{ marginTop: 8, opacity: actif && index !== stepIndex ? 0.6 : 1 }}>
          <strong>
            {index + 1}. {pickText(step.label, language) ?? step.id}
          </strong>
          <div>
            {formatClock(secondes)} · {mots} mots · {cues.filter((c) => c.type === "sceneShot").length} plans
          </div>
          {cues.map((cue, i) =>
            cue.type === "sceneShot" ? (
              <div key={i} style={{ paddingLeft: 10 }}>
                {formatClock(cue.time)} → {pickText(tour.scenes.find((s) => s.id === cue.sceneId)?.title, language) ?? cue.sceneId}
                {" "}(cap {Math.round((cue.keyframes?.[0] ?? cue.start)?.yaw ?? 0)}° →{" "}
                {Math.round((cue.keyframes?.at(-1) ?? cue.end)?.yaw ?? 0)}°
                {cue.keyframes ? `, ${cue.keyframes.length} keyframes` : ""})
              </div>
            ) : null,
          )}
        </div>
      ))}
    </details>
  );
}
